import type { OosDateRange, OosTagMatch } from './services/LurkerApiService';

export const LURKER_ROOT = '/lurker';
/** Strategies compared over the labelled camera-days. */
export const LURKER_DASHBOARD_PATH = `${LURKER_ROOT}/dashboard`;
/** Snapshotted camera-days, and those with results waiting for a snapshot. */
export const LURKER_CAMERA_DAYS_PATH = `${LURKER_ROOT}/camera-days`;
export const LURKER_STRATEGIES_PATH = `${LURKER_ROOT}/strategies`;
/** OOS Labeling: batches of crops frozen from production for bulk labelling. */
export const LURKER_LABELING_PATH = `${LURKER_ROOT}/labeling`;

export const lurkerBatchPath = (batchId: string) =>
  `${LURKER_LABELING_PATH}/${encodeURIComponent(batchId)}`;

/** A batch's labelling grid showing only the crops these labellers labelled last. */
export function lurkerBatchByPath(batchId: string, labellers: string[]) {
  const query = new URLSearchParams(labellers.map((labeller) => ['by', labeller])).toString();
  return `${lurkerBatchPath(batchId)}${query ? `?${query}` : ''}`;
}

/** Labeling stats: who labelled and relabelled which crops, when and where. */
export const LURKER_LABELING_STATS_PATH = `${LURKER_ROOT}/labeling-stats`;

/** Relabel: the shared queue of crops a flag model or a person sent back for another look. */
export const LURKER_RELABEL_PATH = `${LURKER_ROOT}/relabel`;

/** A chain's relabel queue in the labelling grid, e.g. `/lurker/relabel/33?reason=cl`. */
export function lurkerRelabelQueuePath(
  chainId: number,
  params: { status?: string; reason?: string } = {}
) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.reason) query.set('reason', params.reason);
  const search = query.toString();
  return `${LURKER_RELABEL_PATH}/${chainId}${search ? `?${search}` : ''}`;
}

/**
 * The camera-days list filtered to any (or all) of the tags. Without tags it lists the given dates,
 * or the page's default ones.
 */
export function lurkerCameraDaysPath(
  tags: string[],
  tagMatch: OosTagMatch = 'any',
  range?: OosDateRange
) {
  const query = new URLSearchParams(tags.map((tag) => ['tag', tag]));
  if (tags.length && tagMatch === 'all') query.set('tagMatch', 'all');
  if (range) {
    query.set('from', range.firstDate);
    query.set('to', range.lastDate);
  }
  const params = query.toString();
  return params ? `${LURKER_CAMERA_DAYS_PATH}?${params}` : LURKER_CAMERA_DAYS_PATH;
}

export type OosCameraDayView = 'timelines' | 'images';

/** One camera-day in its labelling view. */
export const lurkerCameraDayPath = (cameraDayId: string, view: OosCameraDayView = 'timelines') =>
  `${LURKER_CAMERA_DAYS_PATH}/${encodeURIComponent(cameraDayId)}/${view}`;
